// Certificate pack window (V0.4.8): pick what goes in the one-PDF pack, see it in the page, download it, or go on to
// email it to client finance with the same pages. Line schedule and comment register are off unless ticked.
import { useEffect, useRef, useState } from 'react';
import { api, apiBlob, write } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import type { ClaimDetails, Signature } from '../../lib/claimData';
import { Button, Drawer, errText, useToast } from '../kit';
import { saveBlobUrl } from '../Viewer';
import { docChoices, mainClaimDoc, type PackDocFile } from '../../lib/claimDocs';
import { C3Loader, C3PageLoading, C3Busy } from '../C3Loader';

/** docs (V0.4.12): claim documents bundled after the certificate — null = the default (main claim document only) */
export type PackOpts = { schedule: boolean; variations: boolean; evidence: boolean; register: boolean; docs: string[] | null };
export const PACK_DEFAULTS: PackOpts = { schedule: false, variations: true, evidence: true, register: false, docs: null };
export const packQuery = (o: PackOpts) =>
  (['schedule', 'variations', 'evidence', 'register'] as const).map((k) => `&${k}=${o[k] ? 1 : 0}`).join('') + (o.docs ? `&docs=${o.docs.join(',')}` : '');
type DocFile = PackDocFile & { size_bytes: number | null };
const kb = (n: number | null) => (!n ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function PackBuilder({
  itemId,
  certified,
  evidenceCount,
  variationCount,
  lineCount,
  canEmail,
  onClose,
  onEmail,
  details,
  signatures,
  architectOrgId,
  isSuper = false,
  onChanged,
}: {
  details: ClaimDetails;
  signatures: Signature[];
  architectOrgId: string | null;
  isSuper?: boolean;
  /** reload the claim after issuer / issue changes */
  onChanged: () => void;
  itemId: string;
  certified: boolean;
  evidenceCount: number;
  variationCount: number;
  lineCount: number;
  canEmail: boolean;
  onClose: () => void;
  onEmail: (o: PackOpts) => void;
}) {
  const toast = useToast();
  const [o, setO] = useState<PackOpts>(PACK_DEFAULTS);
  const [orgs, setOrgs] = useState<{ id: string; name: string; kind: string; short_code: string | null }[]>([]);
  const [issuer, setIssuer] = useState<string>(details.issuer_org_id ?? '');
  const [rev, setRev] = useState(0);
  const [swiOnly, setSwiOnly] = useState(false);
  const [reason, setReason] = useState('');
  const [issuing, setIssuing] = useState(false);
  const swiSig = signatures.find((s) => s.kind === 'swi');
  const archSig = signatures.find((s) => s.kind === 'architect');
  const canIssue = canEmail && Boolean(swiSig) && !archSig && !certified;
  // claim documents (V0.4.12): the claim's own PDFs / photos that can go right after the certificate
  const [docs, setDocs] = useState<DocFile[] | null>(null);
  const [mainDoc, setMainDoc] = useState<string | null>(null);
  const [otherFiles, setOtherFiles] = useState(0);
  useEffect(() => {
    void Promise.all([
      supabase
        .from('files')
        .select('id, original_name, mime, size_bytes, uploaded_at, in_pack, response_id')
        .eq('linked_table', 'items')
        .eq('linked_id', itemId)
        .eq('is_current', true)
        .eq('kind', 'file')
        .is('deleted_at', null),
      supabase.from('ai_calls').select('file_ids, purpose, created_at').eq('item_id', itemId).in('purpose', ['claim_read', 'item_read']).order('created_at', { ascending: false }).limit(10),
    ]).then(([f, r]) => {
      const sigFiles = new Set(signatures.map((x) => x.file_id).filter(Boolean));
      const all = ((f.data ?? []) as DocFile[]).filter((x) => !sigFiles.has(x.id));
      const list = docChoices(all);
      const reads = (r.data ?? []) as { file_ids: string[] | null; purpose: string }[];
      const readIds = [...reads.filter((x) => x.purpose === 'claim_read'), ...reads.filter((x) => x.purpose === 'item_read')].map((x) => x.file_ids?.[0]).filter(Boolean) as string[];
      setMainDoc(mainClaimDoc(list, readIds));
      setOtherFiles(all.filter((x) => !x.in_pack).length - list.length);
      setDocs(list);
    });
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps
  const docOn = (id: string) => (o.docs ?? (mainDoc ? [mainDoc] : [])).includes(id);
  const toggleDoc = (id: string, on: boolean) => {
    const cur = o.docs ?? (mainDoc ? [mainDoc] : []);
    const next = on ? [...cur, id] : cur.filter((x) => x !== id);
    // keep the list order (oldest first) so the pack reads the same way as the files
    setO({ ...o, docs: (docs ?? []).map((d) => d.id).filter((x) => next.includes(x)) });
  };
  useEffect(() => {
    void supabase
      .from('organisations')
      .select('id, name, kind, short_code')
      .is('deleted_at', null)
      .order('name')
      .then(({ data }) => setOrgs((data ?? []) as typeof orgs));
  }, []);
  useEffect(() => setIssuer(details.issuer_org_id ?? ''), [details.issuer_org_id]);
  async function pickIssuer(v: string) {
    setIssuer(v);
    try {
      const name = v ? orgs.find((x) => x.id === v)?.name : 'the architect of record';
      await write('claim_details', 'update', { id: details.id, values: { issuer_org_id: v || null }, summary: `Certificate "Issued by" → ${name}` });
      onChanged();
      setRev((r) => r + 1);
    } catch (e) {
      setIssuer(details.issuer_org_id ?? '');
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function issueSwiOnly() {
    setIssuing(true);
    try {
      await api('cert-sign', { action: 'swi_only', item_id: details.item_id, reason: reason.trim() });
      toast({ tone: 'ok', text: 'Issued as final — SWI sign-off only' });
      setSwiOnly(false);
      onChanged();
      setRev((r) => r + 1);
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setIssuing(false);
    }
  }
  const [pdf, setPdf] = useState<{ url: string; name: string } | null>(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');
  const last = useRef<string | null>(null);
  const nativePdf = typeof navigator === 'undefined' || (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled !== false;

  useEffect(() => {
    let live = true;
    setBusy(true);
    setErr('');
    const t = setTimeout(() => {
      apiBlob(`cert-pdf?item_id=${encodeURIComponent(itemId)}${packQuery(o)}`)
        .then((r) => {
          if (!live) return URL.revokeObjectURL(r.url);
          if (last.current) URL.revokeObjectURL(last.current);
          last.current = r.url;
          setPdf(r);
        })
        .catch((e) => live && setErr(errText(e)))
        .finally(() => live && setBusy(false));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [itemId, o, rev, certified]);
  useEffect(() => () => void (last.current && URL.revokeObjectURL(last.current)), []);

  const opt = (k: 'schedule' | 'variations' | 'evidence' | 'register', label: string, help: string, disabled = false) => (
    <label className={`pk-opt ${o[k] && !disabled ? 'on' : ''} ${disabled ? 'off' : ''}`}>
      <input type="checkbox" checked={o[k] && !disabled} disabled={disabled} onChange={(e) => setO({ ...o, [k]: e.target.checked })} />
      <span>
        <b>{label}</b>
        <span className="muted small">{help}</span>
      </span>
    </label>
  );

  return (
    <Drawer open wide title={certified ? 'Certificate pack' : 'Certificate pack — preview (draft)'} onClose={onClose}>
      <div className="pk">
        <div className="pk-side">
          <div className="sw-k">Pages in the pack</div>
          <label className="pk-opt on fixed">
            <input type="checkbox" checked disabled />
            <span>
              <b>Payment certificate</b>
              <span className="muted small">Always — page 1 with the assessment and sign-off</span>
            </span>
          </label>
          <div className="pk-docs" role="group" aria-label="Claim documents">
            <div className="pk-docs-h">
              <b>Claim documents</b>
              <span className="muted small">After the certificate page — the contractor's claim and any substantiating documents</span>
            </div>
            {docs === null ? (
              <p className="muted small c3-inline"><C3Loader size={18} /> Loading the claim's files…</p>
            ) : !docs.length ? (
              <p className="muted small">No PDFs or photos on this claim yet — add them under Files.</p>
            ) : (
              docs.map((d) => (
                <label key={d.id} className={`pk-doc ${docOn(d.id) ? 'on' : ''}`}>
                  <input type="checkbox" checked={docOn(d.id)} onChange={(e) => toggleDoc(d.id, e.target.checked)} aria-label={`Include ${d.original_name}`} />
                  <span className="grow">
                    {d.original_name}
                    {d.id === mainDoc ? <span className="pk-tag">main claim document</span> : null}
                    {d.response_id ? <span className="pk-tag soft">on a response</span> : null}
                  </span>
                  <span className="muted small">{kb(d.size_bytes)}</span>
                </label>
              ))
            )}
            {otherFiles > 0 ? <p className="muted small">{otherFiles} other file{otherFiles === 1 ? '' : 's'} (Word, Excel…) can't go in the PDF — they stay under Files.</p> : null}
          </div>
          {opt('schedule', 'Line schedule', `${lineCount} claim line${lineCount === 1 ? '' : 's'} vs BOQ — off by default`)}
          {opt('variations', 'Variations vs register', variationCount ? `${variationCount} variation${variationCount === 1 ? '' : 's'} with the register check` : 'No variations in this claim', !variationCount)}
          {opt('evidence', 'Progress evidence', evidenceCount ? `${evidenceCount} file${evidenceCount === 1 ? '' : 's'} ticked under Progress evidence` : 'None ticked under Progress evidence', !evidenceCount)}
          {opt('register', 'Comment register', 'Every comment and line note, numbered — off by default')}
          <div className="sw-k" style={{ marginTop: 8 }}>
            Issued by
          </div>
          <label className="pk-issuer">
            <select value={issuer} disabled={!canEmail || (certified && !isSuper)} onChange={(e) => void pickIssuer(e.target.value)} aria-label="Issued by">
              <option value="">{orgs.find((x) => x.id === architectOrgId)?.name ?? 'Architect'} — architect of record (default)</option>
              {[...orgs]
                .filter((x) => x.id !== architectOrgId)
                .sort((a, b) => Number(b.kind === 'internal') - Number(a.kind === 'internal') || a.name.localeCompare(b.name))
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
            </select>
            <span className="muted small">{issuer ? 'Override — the certificate names this company as the issuer' : 'Default: the contract’s architect of record'}</span>
          </label>
          <div className="sw-k" style={{ marginTop: 8 }}>
            Status
          </div>
          <div className={`pk-status ${certified ? 'final' : ''}`}>
            {certified ? (
              details.swi_only ? <b>Final — issued with SWI sign-off only</b> : <b>Final — certified</b>
            ) : (
              <>
                <b>Draft</b>
                <span className="small">{!swiSig ? 'Becomes final when SWI signs and the architect certifies.' : 'SWI has signed — final once the architect certifies, or issue it with SWI sign-off only.'}</span>
              </>
            )}
          </div>
          {canIssue ? (
            <div className="pk-swionly">
              <label className="check">
                <input type="checkbox" checked={swiOnly} onChange={(e) => setSwiOnly(e.target.checked)} /> Architect signature not needed — issue final with SWI sign-off only
              </label>
              {swiOnly ? (
                <>
                  <label className="field">
                    <span className="field-label">Why (kept in the history)</span>
                    <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Deposit claim — PM sign-off only under the contract" aria-label="Reason for SWI-only issue" />
                  </label>
                  <Button kind="primary" disabled={!reason.trim() || issuing} onClick={() => void issueSwiOnly()}>
                    {issuing ? <C3Busy text="Issuing…" accent /> : 'Issue final certificate'}
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
          <div className="pk-actions">
            <Button kind="primary" disabled={!pdf || busy} onClick={() => pdf && saveBlobUrl(pdf.url, pdf.name)}>
              Download PDF
            </Button>
            {canEmail ? (
              <Button disabled={busy} onClick={() => onEmail(o)}>
                Email client finance…
              </Button>
            ) : null}
          </div>
          {pdf ? <p className="muted small">{pdf.name}</p> : null}
        </div>
        <div className="pk-view">
          {err ? (
            <div className="error-box">{err}</div>
          ) : !pdf ? (
            <C3PageLoading label="Making the pack" variant="levels" size={64} />
          ) : nativePdf ? (
            <iframe key={pdf.url} src={pdf.url} title="Certificate pack preview" className={`pk-frame ${busy ? 'stale' : ''}`} />
          ) : (
            <p className="muted">
              This device can't show PDFs inside the page —{' '}
              <a href={pdf.url} target="_blank" rel="noopener">
                open it
              </a>{' '}
              or download it.
            </p>
          )}
          {busy && pdf ? <span className="pk-busy c3-busy"><C3Loader size={16} label="Updating" /> Updating…</span> : null}
        </div>
      </div>
    </Drawer>
  );
}
